import ErrorNote from './ErrorNote'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Award, ChevronRight, Ruler, Search, Users, X } from 'lucide-react'
import {
  activeFilters,
  fetchDancers,
  tenureLabel,
  type DancerFilter,
  type DancerRow,
  type DancerSort,
  type DancerTeacherOption,
} from '../api/dancerAdmin'

const STATUSES = [
  { key: 'active', label: 'Dancing now' },
  { key: 'former', label: 'Former' },
  { key: 'all', label: 'Everyone' },
]

const PROGRAMS = [
  { key: 'comp', label: 'Competitive' },
  { key: 'rec', label: 'Recreational' },
] as const

const SORTS: { key: DancerSort; label: string }[] = [
  { key: 'name', label: 'Surname (A–Z)' },
  { key: 'age', label: 'Youngest first' },
  { key: 'tenure', label: 'Longest with the studio' },
  { key: 'classes', label: 'Most classes' },
]

const AWARDS = [5, 10, 15]

/**
 * The whole studio, as dancers.
 *
 * <b>Not a milestone report.</b> The 5/10/15-year recital awards are what
 * prompted the screen and they are one filter on it — building only for them
 * would produce something Tonya opens once a year, when the questions she
 * actually has ("what size is she?", "which classes is she in?", "how old is
 * she?") are about a child and are asked constantly (D-151).
 *
 * Everything is listed before anything is typed (D-127): a screen that shows
 * nothing until you search makes somebody guess what is searchable first.
 *
 * <b>735 names is more than anyone can read</b>, which is what the filters are
 * for (D-183). They are grouped by the question they answer — who is here, what
 * do they dance, who teaches them, how should this be ordered — rather than run
 * together as one long row of buttons, because a wall of controls is its own
 * kind of overwhelming.
 */
export default function DancerAdminView() {
  const [filter, setFilter] = useState<DancerFilter>({ status: 'active' })

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dancers', filter],
    queryFn: () => fetchDancers(filter),
    // Keeps the list on screen while the next query runs, so changing a filter
    // does not flash the page empty and lose your scroll position.
    placeholderData: (previous) => previous,
  })

  const set = (patch: Partial<DancerFilter>) => setFilter((f) => ({ ...f, ...patch }))

  const award = filter.milestone
  const teachers = data?.teachers ?? []
  const applied = activeFilters(filter, teachers)

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Dancers</p>
        {/* Was `text-[22px] font-bold` — sans and bold where the house heading
            is serif at `text-2xl`. The two dancer screens were the only pair
            using that idiom (D-335). */}
        <h1 className="mt-0.5 font-display text-2xl">The studio, dancer by dancer</h1>
        {data && (
          <p className="mt-1 text-sm text-ink-3">
            {data.dancers.length === data.total
              ? `${data.total} dancers`
              : `${data.dancers.length} of ${data.total} dancers`}
            {data.tenureUnknown > 0 && (
              <>
                {' · '}
                <span title="No start date in Jackrabbit, so no tenure can be worked out">
                  {data.tenureUnknown} with no start date
                </span>
              </>
            )}
          </p>
        )}
      </header>

      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              size={15}
              strokeWidth={2.5}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
            />
            <input
              value={filter.q ?? ''}
              onChange={(e) => set({ q: e.target.value })}
              placeholder="Find a dancer by name"
              className="w-full rounded-lg border border-line-2 bg-surface-2 py-2.5 pr-3 pl-9 text-[14px] text-ink outline-none placeholder:text-ink-3 focus:border-accent focus:bg-surface"
            />
          </div>

          <Picker
            label="Sort by"
            value={filter.sort ?? 'name'}
            onChange={(value) => set({ sort: value as DancerSort })}
            options={SORTS.map((s) => ({ value: s.key, label: s.label }))}
          />
        </div>

        <FilterRow label="Showing">
          {STATUSES.map((s) => (
            <Pill
              key={s.key}
              on={(filter.status ?? 'active') === s.key}
              onClick={() => set({ status: s.key })}
            >
              {s.label}
            </Pill>
          ))}
        </FilterRow>

        <FilterRow label="Programme">
          {PROGRAMS.map((p) => (
            <Pill
              key={p.key}
              on={filter.program === p.key}
              onClick={() => set({ program: filter.program === p.key ? undefined : p.key })}
            >
              {p.label}
            </Pill>
          ))}

          {/*
            A dropdown rather than pills: there are enough teachers that a row
            of them would wrap three deep and bury everything under it.
          */}
          <TeacherPicker
            teachers={teachers}
            value={filter.teacher}
            onChange={(teacher) => set({ teacher })}
          />
        </FilterRow>

        <FilterRow label="Recital awards">
          {/*
            Award years as their own filter rather than a sort. "Who is due a
            10-year award?" is a question with a short, specific answer, and
            sorting 735 dancers by tenure buries it under everyone at four.
          */}
          {AWARDS.map((years) => (
            <Pill
              key={years}
              tone="good"
              on={award === years}
              onClick={() =>
                set({ milestone: award === years ? undefined : years, milestonesOnly: false })
              }
            >
              <Award size={13} strokeWidth={2.5} />
              {years} years
            </Pill>
          ))}

          <Pill
            tone="good"
            on={filter.milestonesOnly === true}
            onClick={() => set({ milestonesOnly: !filter.milestonesOnly, milestone: undefined })}
          >
            Any award year
          </Pill>
        </FilterRow>

        {/*
          Says what is currently hiding people, and offers to stop. Without it,
          "Nobody matches" after a forgotten teacher filter looks like the data
          is missing rather than the filter being on.
        */}
        {applied.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line pt-3 text-[12.5px] text-ink-3">
            <span>Showing {applied.join(' · ')}</span>
            <button
              type="button"
              onClick={() => setFilter({ status: 'active' })}
              className="inline-flex items-center gap-1 font-semibold text-accent hover:underline"
            >
              <X size={12} strokeWidth={3} />
              Clear filters
            </button>
          </div>
        )}
      </div>

      {isPending && <p className="text-sm text-ink-3">Loading the studio...</p>}

      {isError && (
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      )}

      {data && data.dancers.length === 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-5 py-6">
          <Users size={22} strokeWidth={2} className="shrink-0 text-ink-3" />
          <div>
            <p className="text-[14px] font-semibold">Nobody matches</p>
            <p className="mt-0.5 text-[13px] text-ink-3">
              {applied.length > 0
                ? 'Every filter above narrows the list further. Clearing them shows the studio again.'
                : 'Nothing to show — import from Jackrabbit to bring the studio across.'}
            </p>
          </div>
        </div>
      )}

      {data && data.dancers.length > 0 && (
        <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {data.dancers.map((d) => (
            <DancerListRow key={d.id} dancer={d} />
          ))}
        </ul>
      )}
    </div>
  )
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Fixed width so the three rows of pills line up down the left edge. */}
      <span className="w-[6.5rem] shrink-0 text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {label}
      </span>
      {children}
    </div>
  )
}

function Pill({
  on,
  tone = 'accent',
  onClick,
  children,
}: {
  on: boolean
  tone?: 'accent' | 'good'
  onClick: () => void
  children: React.ReactNode
}) {
  const active = tone === 'good' ? 'border-good bg-good-bg text-good' : 'border-accent bg-accent-soft text-accent-2'

  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold ${
        on ? active : 'border-line-2 text-ink-3 hover:border-ink-3'
      }`}
    >
      {children}
    </button>
  )
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="flex shrink-0 items-center gap-2 rounded-lg border border-line-2 bg-surface-2 py-2 pr-2 pl-3 focus-within:border-accent">
      <span className="text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">{label}</span>
      {/*
        `bg-surface-2`, never `bg-transparent`. Chromium renders the native
        dropdown using the select's own background, so an explicitly
        transparent one falls back to white — while the options keep `text-ink`,
        which is near-white in dark mode. The list was there and invisible.
      */}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-surface-2 text-[13px] font-semibold text-ink outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function TeacherPicker({
  teachers,
  value,
  onChange,
}: {
  teachers: DancerTeacherOption[]
  value: string | undefined
  onChange: (staffId: string | undefined) => void
}) {
  // Hidden rather than shown empty before the first load lands, so it never
  // offers a choice of nothing.
  if (teachers.length === 0) return null

  return (
    <Picker
      label="Teacher"
      value={value ?? ''}
      onChange={(next) => onChange(next || undefined)}
      options={[
        { value: '', label: 'Anyone' },
        ...teachers.map((t) => ({ value: t.staffId, label: `${t.name} (${t.dancers})` })),
      ]}
    />
  )
}

function DancerListRow({ dancer }: { dancer: DancerRow }) {
  return (
    <li>
      <Link
        to={`/admin/dancers/${dancer.id}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2"
      >
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold wrap-anywhere">
            {dancer.firstName} {dancer.lastName}
            {/*
              Comp is called out and rec is not (D-184). Chipping both would put
              a badge on every one of 735 rows, which is the same as chipping
              none — and comp is the one that carries the 80% rule.
            */}
            {dancer.isComp && (
              <span className="ml-2 rounded-md bg-accent-soft px-1.5 py-0.5 align-middle text-[11px] font-bold text-accent-2">
                Comp
              </span>
            )}
            {dancer.milestone !== null && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-good-bg px-1.5 py-0.5 align-middle text-[11px] font-bold text-good">
                <Award size={11} strokeWidth={3} />
                {dancer.milestone} years
              </span>
            )}
            {dancer.status !== 'Active' && (
              <span className="ml-2 rounded-md bg-surface-2 px-1.5 py-0.5 align-middle text-[11px] font-semibold text-ink-3">
                {dancer.status}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            <span className="tabular">{dancer.age}</span> &middot; {dancer.householdName} family
            {dancer.classes > 0 && (
              <>
                {' '}
                &middot; <span className="tabular">{dancer.classes}</span>{' '}
                {dancer.classes === 1 ? 'class' : 'classes'}
              </>
            )}
          </p>
        </div>

        {dancer.hasMeasurements && (
          <Ruler
            size={14}
            strokeWidth={2.5}
            className="shrink-0 text-ink-3"
            aria-label="Costume sizes on file"
          />
        )}

        <span
          className={`shrink-0 text-[12.5px] font-semibold tabular ${
            dancer.years === null ? 'text-ink-3 italic' : 'text-ink-2'
          }`}
        >
          {tenureLabel(dancer.years)}
        </span>

        <ChevronRight size={15} strokeWidth={2.5} className="shrink-0 text-ink-3" />
      </Link>
    </li>
  )
}
